import { configureStore } from '@reduxjs/toolkit';
import { oidcApi } from './oidc.api.js';
import { wellknownApi } from './wellknown.api.js';
/**
 * @function createClientStore
 * @description Creates a Redux store configured with OIDC and well-known APIs.
 * @param param - Configuration options for the client store.
 * @param {RequestMiddleware} param.requestMiddleware - An array of request middleware functions to be applied to the store.
 * @param {ReturnType<typeof loggerFn>} param.logger - An optional logger function for logging messages.
 * @returns { ReturnType<typeof configureStore> } - Returns a configured Redux store with OIDC and well-known APIs.
 */
export function createClientStore({ requestMiddleware, logger, }) {
    return configureStore({
        reducer: {
            [oidcApi.reducerPath]: oidcApi.reducer,
            [wellknownApi.reducerPath]: wellknownApi.reducer,
        },
        middleware: (getDefaultMiddleware) => getDefaultMiddleware({
            thunk: {
                extraArgument: {
                    /**
                     * This becomes the `api.extra` argument, and will be passed into the
                     * customer query wrapper for `baseQuery`
                     */
                    requestMiddleware,
                    logger,
                },
            },
        })
            .concat(wellknownApi.middleware)
            .concat(oidcApi.middleware),
    });
}
/**
 * @function createLogoutError
 * @description Creates a logout error object based on the provided data and error.
 * @param  {object | null | undefined} data - The data returned from the logout API call.
 * @param {FetchBaseQueryError | SerializedError} error - An optional error object that may contain details about the error that occurred during the logout process.
 * @returns {null | GenericError} - Returns a `GenericError` object if an error occurred, or `null` if no error is present.
 */
export function createLogoutError(data, error) {
    if (error) {
        let message = 'An error occurred while ending the session';
        let status = 'unknown';
        if ('message' in error && error.message) {
            message = error.message;
        }
        if ('status' in error) {
            status = error.status;
        }
        return {
            error: 'End Session failure',
            message,
            type: 'auth_error',
            status,
        };
    }
    return null;
}
export function createTokenError(type) {
    let error;
    if (type === 'no_tokens') {
        error = {
            error: 'Token_Error',
            message: 'Required for ending session and revoking access token',
            type: 'state_error',
        };
    }
    else if (type === 'no_access_token') {
        error = {
            error: 'Token_Error',
            message: 'No access token found in storage; required for revoking access token',
            type: 'state_error',
        };
    }
    else if (type === 'no_id_token') {
        error = {
            error: 'Token_Error',
            message: 'No ID token found in storage; required for ending session',
            type: 'state_error',
        };
    }
    else {
        error = {
            error: 'Token_Error',
            message: 'An unknown error occurred while creating the error object',
            type: 'unknown_error',
        };
    }
    return error;
}
//# sourceMappingURL=client.store.utils.js.map